(ns practitest.frontend.components2.dashboard.svg
  (:require ["d3" :as d3]))

(def event-map
  "Maps hiccup event attr keys to DOM event names for D3's .on()."
  {:on-click        "click"
   :on-double-click "dblclick"
   :on-mouse-enter  "mouseenter"
   :on-mouse-leave  "mouseleave"
   :on-mouse-over   "mouseover"
   :on-mouse-out    "mouseout"
   :on-mouse-move   "mousemove"
   :on-mouse-down   "mousedown"
   :on-mouse-up     "mouseup"})

;; SVG hiccup DSL rendered via D3.
;;
;; Supported tags:
;;   :svg / :svg/g        — group; two coordinate systems via :view-box:
;;                            {:view-box [w h]}            — cartesian: (0,0) at top-left;
;;                            {:view-box [:svg/polar r]}   — polar: viewBox is 2r x 2r, origin (0,0) moved to center by translate(r,r).
;;                                                           Coordinates are still x/y, but :svg/arc and :svg/line accept (angle,r)
;;   :<>                  — fragment
;;   :svg/center          — renders children centered at (0,0) in polar, or at viewBox center in cartesian
;;   :svg/zstack          — layers all children at (0,0); use inside polar or cartesian, children overlap
;;   :svg/hstack          — horizontal layout with optional {:gap :align}
;;   :svg/vstack          — vertical layout with optional {:gap :align}
;;   :svg/transition      — animates children from :from to :to; accepts {:duration ms}
;;   :svg/arc             — arc path; {:from :to :radius :weight} + optional {:scale [s e] :corner-r}
;;   :svg/line            — line; cartesian {:x1 :y1 :x2 :y2} or polar {:angle :length} or {:angle :offset :length}
;;   :svg/arc.cls etc.    — dot-syntax adds CSS class; works on any :svg/* tag
;;   everything else      — rendered as native SVG/HTML element via D3
;;
;; Rendering pipeline:
;;   render!        — entry point; sets viewBox, clears DOM, delegates to render-hiccup!
;;   render-hiccup! — dispatches on hiccup type (nil/string/seq/vector); vectors go to render-svg!
;;   render-svg!    — multimethod; mutates DOM for structural tags; delegates :svg/* shapes to resolve-tag
;;   resolve-tag    — multimethod; resolves :svg/* shape tags (arc, line) to native hiccup
;;   transition!    — multimethod; animates a previously rendered shape element via D3 transition

(declare render-hiccup! viewbox-center viewbox-dims)

(def arc-keys
  #{:from :to :radius :inner-r :outer-r :weight :corner-r})

(def line-keys
  #{:angle :offset :length :x1 :y1 :x2 :y2})

(defmulti transition!
  "Animate a rendered element. Dispatches on the svg tag keyword."
  (fn [tag _el _attrs _duration _ctx] tag))

(defmulti resolve-tag
  "Resolve an :svg/* shape tag to native hiccup. Dispatches on base tag."
  (fn [tag _attrs _ctx] tag))

(defmulti render-svg!
  "Render a hiccup vector tag into parent. Dispatches on tag keyword."
  (fn [_parent tag _rest _ctx] tag))

(defn render!
  "Clear svg-element and render svg-hiccup into it.
   :view-box [w h]          — standard viewBox, origin at top-left.
   :view-box [:svg/polar r] — polar viewBox, origin shifted to center; sets viewBox to 2r x 2r."
  [svg-el [_tag {vb :view-box} :as svg-hiccup]]
  (let [[w h] (viewbox-dims vb)]
    (when (and w h)
      (.setAttribute svg-el "viewBox" (str "0 0 " w " " h))))
  (set! (.-innerHTML svg-el) "")
  (render-hiccup! svg-el svg-hiccup nil))

(defn render-hiccup!
  "Render hiccup to SVG DOM via D3. Handles elements, fragments, seqs, and text."
  ([parent hiccup] (render-hiccup! parent hiccup nil))
  ([parent hiccup ctx]
   (cond
     (nil? hiccup) nil

     (or (string? hiccup) (number? hiccup))
     (-> (.select d3 parent) (.text (str hiccup)))

     (seq? hiccup)
     (doseq [child hiccup]
       (render-hiccup! parent child ctx))

     (vector? hiccup)
     (let [[tag & rest] hiccup]
       (render-svg! parent tag rest ctx)))))

;; --- render-svg! defmethods ---

(defmethod render-svg! :<> [parent _ rest ctx]
  (doseq [child rest] (render-hiccup! parent child ctx)))

(defmethod render-svg! :svg/g [parent _ rest ctx]
  (let [[attrs & children] (if (map? (first rest)) rest (cons nil rest))
        vb     (:view-box attrs)
        polar? (= :svg/polar (first vb))
        ctx    (cond-> ctx
                 (:scale attrs) (assoc :scale (:scale attrs))
                 polar?         (assoc :in-polar? true :view-box vb))]
    (if polar?
      (let [r (second vb)
            g (-> (.select d3 parent) (.append "g"))]
        (.attr g "transform" (str "translate(" r "," r ")"))
        (doseq [child children] (render-hiccup! (.node g) child ctx)))
      (doseq [child children] (render-hiccup! parent child ctx)))))

(defmethod render-svg! :svg [parent _ rest ctx]
  (render-svg! parent :svg/g rest ctx))

(defmethod render-svg! :svg/center [parent _ rest ctx]
  (let [[_attrs & children] (if (map? (first rest)) rest (cons nil rest))
        [cx cy] (if (:in-polar? ctx) [0 0] (viewbox-center parent))
        g       (-> (.select d3 parent) (.append "g"))]
    (doseq [child children] (render-hiccup! (.node g) child ctx))
    (let [bbox (.getBBox (.node g))
          dx   (- cx (+ (.-x bbox) (/ (.-width bbox) 2)))
          dy   (- cy (+ (.-y bbox) (/ (.-height bbox) 2)))]
      (.attr g "transform" (str "translate(" dx "," dy ")")))))

(defmethod render-svg! :svg/zstack [parent _ rest ctx]
  (let [[_attrs & children] (if (map? (first rest)) rest (cons nil rest))]
    (doseq [child children]
      (render-hiccup! parent child ctx))))

(defmethod render-svg! :svg/transition [parent _ rest ctx]
  (letfn [(render-child! [g child duration]
            (let [[child-tag & crest] child
                  [cattrs]      (if (map? (first crest)) crest (cons {} crest))
                  base-tag      (keyword (namespace child-tag)
                                         (first (.split (name child-tag) ".")))
                  initial-child (assoc (vec child) 1 (assoc cattrs :to (:from cattrs)))]
              (render-hiccup! (.node g) initial-child ctx)
              (let [el (-> (.select d3 (.node g)) (.select ":last-child") (.node))]
                (transition! base-tag el cattrs duration ctx))))]
    (let [[attrs & children] (if (map? (first rest)) rest (cons nil rest))
          {:keys [duration] :or {duration 600}} attrs
          g (-> (.select d3 parent) (.append "g"))]
      (doseq [child children]
        (when (vector? child) (render-child! g child duration))))))

(defmethod render-svg! :svg/hstack [parent _ rest ctx]
  (let [[attrs & children] (if (map? (first rest)) rest (cons nil rest))
        {:keys [gap align translate-y] :or {gap 0 align :center}} attrs
        container (-> (.select d3 parent) (.append "g"))]
    (when translate-y (.attr container "transform" (str "translate(0," translate-y ")")))
    (let [child-ctx (dissoc ctx :in-polar?)
          wrappers  (doall (for [child children]
                             (let [g (.append container "g")]
                               (render-hiccup! (.node g) child child-ctx)
                               g)))]
      (loop [[w & more] wrappers pos 0]
        (when w
          (let [bbox    (.getBBox (.node w))
                mid-y   (+ (.-y bbox) (/ (.-height bbox) 2))
                dx      (- pos (.-x bbox))
                dy      (case align :center (- mid-y) 0)
                advance (+ (.-width bbox) gap)]
            (.attr w "transform" (str "translate(" dx "," dy ")"))
            (recur more (+ pos advance)))))
      (when (:in-polar? ctx)
        (let [bbox (.getBBox (.node container))]
          (.attr container "transform"
                 (str "translate(" (- (+ (.-x bbox) (/ (.-width bbox) 2)))
                      "," (- (+ (.-y bbox) (/ (.-height bbox) 2))) ")")))))))

(defmethod render-svg! :svg/vstack [parent _ rest ctx]
  (let [[attrs & children] (if (map? (first rest)) rest (cons nil rest))
        {:keys [gap align translate-y] :or {gap 0 align :center}} attrs
        container (-> (.select d3 parent) (.append "g"))]
    (when translate-y (.attr container "transform" (str "translate(0," translate-y ")")))
    (let [child-ctx (dissoc ctx :in-polar?)
          wrappers  (doall (for [child children]
                             (let [g (.append container "g")]
                               (render-hiccup! (.node g) child child-ctx)
                               g)))]
      (loop [[w & more] wrappers pos 0]
        (when w
          (let [bbox    (.getBBox (.node w))
                mid-x   (+ (.-x bbox) (/ (.-width bbox) 2))
                dx      (case align :center (- mid-x) (- (.-x bbox)))
                dy      (- pos (.-y bbox))
                advance (+ (.-height bbox) gap)]
            (.attr w "transform" (str "translate(" dx "," dy ")"))
            (recur more (+ pos advance)))))
      (when (:in-polar? ctx)
        (let [bbox (.getBBox (.node container))]
          (.attr container "transform"
                 (str "translate(" (- (+ (.-x bbox) (/ (.-width bbox) 2)))
                      "," (- (+ (.-y bbox) (/ (.-height bbox) 2))) ")")))))))

(defmethod render-svg! :default [parent tag rest ctx]
  (if (= "svg" (namespace tag))
    (let [tag-str          (name tag)
          [base & classes] (.split tag-str ".")
          base-tag         (keyword "svg" base)
          [attrs & _]      (if (map? (first rest)) rest (cons {} rest))
          attrs            (cond-> attrs
                             (seq classes) (assoc :class (.join (clj->js classes) " ")))]
      (if (get-method resolve-tag base-tag)
        (render-hiccup! parent (resolve-tag base-tag attrs ctx) ctx)
        (render-hiccup! parent (into [(keyword base)] rest) ctx)))
    (let [tag-str             (name tag)
          [el-name & classes] (.split tag-str ".")
          [attrs & children]  (if (map? (first rest)) rest (cons nil rest))
          {ty :translate-y :as attrs} attrs
          attrs               (dissoc attrs :translate-y)
          el                  (-> (.select d3 parent) (.append el-name))]
      (when (seq classes) (.attr el "class" (.join (clj->js classes) " ")))
      (when ty (.attr el "transform" (str "translate(0," ty ")")))
      (doseq [[k v] attrs]
        (if-let [event (event-map k)]
          (.on el event v)
          (.attr el (name k) v)))
      (doseq [child children] (render-hiccup! (.node el) child ctx)))))

;; --- helpers ---

(defn- viewbox-center
  "Compute visual center [cx cy] from the root <svg> viewBox."
  [el]
  (let [svg (or (.-ownerSVGElement el) el)
        vb  (.. svg -viewBox -baseVal)]
    [(+ (.-x vb) (/ (.-width vb) 2))
     (+ (.-y vb) (/ (.-height vb) 2))]))

(defn- viewbox-dims
  "Returns [w h] for a :view-box value.
   [:svg/polar r]       — expands to [2r 2r].
   [:svg/cartesian w h] or [w h] — returns [w h] as-is."
  [[vb-type :as vb]]
  (if (= vb-type :svg/polar)
    (let [[_ r] vb]
      [(* 2 r) (* 2 r)])
    vb))

(defn- arc-scale
  "Returns fn mapping [0..1] to angle between start-angle and end-angle."
  [start-angle end-angle]
  (-> (d3/scaleLinear)
      (.domain #js [0 1])
      (.range #js [start-angle end-angle])))

(defn- angle->xy [angle r]
  (vec (d3/pointRadial angle r)))

(defn- resolve-t
  "If value is [:t n], apply scale. Otherwise return as-is. Nil scale is a no-op."
  [value scale]
  (if (and scale (vector? value) (= :t (first value)))
    (scale (second value))
    value))

;; --- resolve-tag defmethods ---

(defmethod resolve-tag :svg/arc [_ attrs ctx]
  (let [[_ geo-r] (:view-box ctx)
        scale     (or (:scale ctx)
                      (when-let [[s e] (:scale attrs)]
                        (arc-scale s e)))
        attrs     (cond-> (dissoc attrs :scale)
                    (and geo-r (not (:outer-r attrs)) (not (:radius attrs))) (assoc :radius geo-r))
        attrs     (-> attrs (update :from resolve-t scale) (update :to resolve-t scale))
        {:keys [from to radius inner-r outer-r weight corner-r]} attrs
        outer-r   (or outer-r radius)
        inner-r   (or inner-r (- outer-r weight))
        svg-attrs (apply dissoc attrs arc-keys)
        gen       (-> (d3/arc) (.innerRadius inner-r) (.outerRadius outer-r) (.cornerRadius (or corner-r 0)))]
    [:path (merge {:d (gen #js {:startAngle from :endAngle to})} svg-attrs)]))

(defmethod resolve-tag :svg/line [_ attrs ctx]
  (let [scale     (or (when-let [[s e] (:scale attrs)]
                        (arc-scale s e))
                      (:scale ctx))
        attrs     (cond-> (dissoc attrs :scale)
                    scale
                    (cond-> (:angle attrs) (update :angle resolve-t scale)))
        {:keys [angle offset length x1 y1 x2 y2]} attrs
        svg-attrs (apply dissoc attrs line-keys)
        [x1 y1 x2 y2] (if (and angle length)
                         (let [o (or offset 0)
                               [x1 y1] (angle->xy angle o)
                               [x2 y2] (angle->xy angle (+ o length))]
                           [x1 y1 x2 y2])
                         [x1 y1 x2 y2])]
    [:line (merge {:x1 x1 :y1 y1 :x2 x2 :y2 y2} svg-attrs)]))

;; --- transition! defmethods ---

(defmethod transition! :svg/arc
  [_ el {:keys [from to radius weight inner-r outer-r corner-r scale]} duration ctx]
  (let [scale   (or (:scale ctx)
                    (when (vector? scale)
                      (arc-scale (first scale) (second scale))))
        outer-r (or outer-r radius (when (:in-polar? ctx) (second (:view-box ctx))))
        inner-r (or inner-r (- outer-r weight))
        from    (resolve-t from scale)
        to      (resolve-t to scale)
        arc-gen (-> (d3/arc)
                    (.innerRadius inner-r)
                    (.outerRadius outer-r)
                    (.cornerRadius (or corner-r 0)))]
    (-> (.select d3 el)
        (.transition)
        (.duration duration)
        (.attrTween "d"
                    (fn [_]
                      (let [interp (.interpolate d3 from to)]
                        (fn [t]
                          (arc-gen #js {:startAngle from
                                        :endAngle   (interp t)}))))))))
